// components/GuardEditForm.jsx
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import FormInput from "./FormInput";
import SubmitButton from "./SubmitButton";
import { updateGuard } from "../api/vipform";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function GuardEditForm({ guardData, onBack }) {
  const role = localStorage.getItem("role");

  const [guardformData, setGuardFormData] = useState({
    ...guardData,
    password: "",
  });

  const [ranks, setRanks] = useState([]);
  const [otherRank, setOtherRank] = useState("");
  const [showOtherRank, setShowOtherRank] = useState(false);

  /* FETCH DYNAMIC RANKS (ADMIN ONLY) */
  useEffect(() => {
    if (role !== "admin") return;

    const fetchRanks = async () => {
      try {
        const token = localStorage.getItem(`${role}Token`);

        const response = await axios.get(
          `${BASE_URL}/api/officer/unique-ranks`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const fetchedRanks = response.data || [];
        setRanks(fetchedRanks);

        if (guardData.rank && !fetchedRanks.includes(guardData.rank)) {
          setShowOtherRank(true);
          setOtherRank(guardData.rank);
          setGuardFormData((prev) => ({ ...prev, rank: "Other" }));
        }
      } catch (error) {
        console.error("Failed to fetch ranks:", error);
        toast.error("Unable to load ranks!");
      }
    };

    fetchRanks();
  }, [guardData.rank, role]);

  /* INPUT CHANGE */
  const handle_gd_change = (e) => {
    const { name, value } = e.target;

    if (name === "rank") {
      setGuardFormData((prev) => ({ ...prev, rank: value }));
      setShowOtherRank(value === "Other");
      if (value !== "Other") setOtherRank("");
      return;
    }

    setGuardFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* SUBMIT */
  const handle_gd_submit = async (e) => {
    e.preventDefault();

    const finalData = {
      ...guardformData,
      rank: guardformData.rank === "Other" ? otherRank : guardformData.rank,
    };

    if (!finalData.password) delete finalData.password;

    try {
      await updateGuard(finalData.id, finalData);
      toast.success("Officer updated successfully!");
      onBack();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update Officer");
    }
  };

  return (
    <div className="guardedit-wrapper">
      <div className="guardedit-card">
        <div className="card-header">
          <h1>
            <i className="fas fa-user-shield"></i> Edit Officer Profile
          </h1>
          <p>Update officer details securely and efficiently.</p>
        </div>

        <form onSubmit={handle_gd_submit} className="guard-form">
          <FormInput
            label="Full Name"
            name="name"
            value={guardformData.name}
            onChange={handle_gd_change}
            isEdit
          />

          <FormInput
            label="Email"
            name="email"
            value={guardformData.email}
            onChange={handle_gd_change}
            isEdit
          />

          <FormInput
            label="Username"
            name="username"
            value={guardformData.username}
            onChange={handle_gd_change}
            isEdit
          />

          <FormInput
            label="New Password (leave blank to keep current)"
            name="password"
            type="password"
            value={guardformData.password}
            onChange={handle_gd_change}
            isEdit
            placeholder="••••••••"
          />

          {role === "admin" && (
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-medal"></i> Officer Rank
              </label>
              <select
                name="rank"
                value={guardformData.rank || ""}
                onChange={handle_gd_change}
                className="rank-select"
                required
              >
                <option value="">-- Select Rank --</option>
                {ranks.map((rank) => (
                  <option key={rank} value={rank}>
                    {rank}
                  </option>
                ))}
                <option value="Other">Other (Custom Rank)</option>
              </select>
            </div>
          )}

          {showOtherRank && role === "admin" && (
            <FormInput
              label="Custom Rank"
              name="otherRank"
              value={otherRank}
              onChange={(e) => setOtherRank(e.target.value)}
              isEdit
              placeholder="Enter custom rank"
            />
          )}

          <FormInput
            label="Guard Experience (Years)"
            name="experience"
            type="text"
            value={guardformData.experience || ""}
            onChange={handle_gd_change}
            isEdit
          />

          <FormInput
            label="Contact Number"
            name="contactno"
            value={guardformData.contactno}
            onChange={handle_gd_change}
            isEdit
          />

          <div className="button-group">
            <SubmitButton label="Update Officer" />

            <button
              type="button"
              onClick={onBack}
              className="cancel-btn"
            >
              <i className="fas fa-arrow-left"></i> Cancel & Go Back
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .guardedit-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem 1rem;
        }

        .guardedit-card {
          width: 100%;
          max-width: 600px;
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(16px);
          border-radius: 20px;
          padding: 3rem 2.5rem;
          border: 1px solid rgba(59, 130, 246, 0.3);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
          transition: all 0.3s ease;
        }

        .guardedit-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 25px 50px rgba(59, 130, 246, 0.2);
        }

        .card-header {
          text-align: center;
          margin-bottom: 2.5rem;
        }

        .card-header h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #60a5fa;
          margin-bottom: 0.8rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.8rem;
        }

        .card-header p {
          opacity: 0.85;
          font-size: 1.05rem;
        }

        .guard-form {
          display: flex;
          flex-direction: column;
          gap: 1.6rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-label {
          font-weight: 600;
          margin-bottom: 0.6rem;
          color: #cbd5e1;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .rank-select {
          padding: 0.9rem 1rem;
          border-radius: 12px;
          border: 1px solid rgba(59, 130, 246, 0.5);
          background: rgba(15, 23, 42, 0.8);
          color: #e2e8f0;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .rank-select:focus {
          outline: none;
          border-color: #60a5fa;
          box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
        }

        .button-group {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-top: 2rem;
        }

        .cancel-btn {
          background: transparent;
          color: #94a3b8;
          border: 2px solid #475569;
          padding: 0.9rem 1.5rem;
          border-radius: 14px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
        }

        .cancel-btn:hover {
          background: #475569;
          color: white;
          border-color: #60a5fa;
          transform: translateY(-2px);
        }

        @media (max-width: 576px) {
          .guardedit-card {
            padding: 2rem 1.5rem;
            margin: 0 1rem;
          }

          .card-header h1 {
            font-size: 1.7rem;
          }

          .card-header p {
            font-size: 0.95rem;
          }

          .button-group {
            margin-top: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
}

GuardEditForm.propTypes = {
  guardData: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
};



// import React, { useState, useEffect } from "react";
// import PropTypes from "prop-types";
// import axios from "axios";
// import FormInput from "./FormInput";
// import SubmitButton from "./SubmitButton";
// import { updateGuard } from "../api/vipform";
// import { toast } from "react-toastify";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function GuardEditForm({ guardData, onBack }) {
//   const role = localStorage.getItem("role");

//   const [guardformData, setGuardFormData] = useState({
//     ...guardData,
//     password: "",
//   });

//   const [ranks, setRanks] = useState([]);
//   const [otherRank, setOtherRank] = useState("");
//   const [showOtherRank, setShowOtherRank] = useState(false);

//   /* -------- FETCH DYNAMIC RANKS (ADMIN ONLY) -------- */
//   useEffect(() => {
//     if (role !== "admin") return;

//     const fetchRanks = async () => {
//       try {
//         const token = localStorage.getItem(`${role}Token`);

//         const response = await axios.get(
//           `${BASE_URL}/api/officer/unique-ranks`,
//           {
//             headers: { Authorization: `Bearer ${token}` },
//           }
//         );

//         const fetchedRanks = response.data || [];
//         setRanks(fetchedRanks);

//         if (guardData.rank && !fetchedRanks.includes(guardData.rank)) {
//           setShowOtherRank(true);
//           setOtherRank(guardData.rank);
//           setGuardFormData((prev) => ({ ...prev, rank: "Other" }));
//         }
//       } catch (error) {
//         console.error("Failed to fetch ranks:", error);
//         toast.error("Unable to load ranks!");
//       }
//     };

//     fetchRanks();
//   }, [guardData.rank, role]);

//   /* -------- INPUT CHANGE -------- */
//   const handle_gd_change = (e) => {
//     const { name, value } = e.target;

//     if (name === "rank") {
//       setGuardFormData((prev) => ({ ...prev, rank: value }));
//       setShowOtherRank(value === "Other");
//       if (value !== "Other") setOtherRank("");
//       return;
//     }

//     setGuardFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   /* -------- SUBMIT -------- */
//   const handle_gd_submit = async (e) => {
//     e.preventDefault();

//     const finalData = {
//       ...guardformData,
//       rank: guardformData.rank === "Other" ? otherRank : guardformData.rank,
//     };

//     if (!finalData.password) delete finalData.password;

//     try {
//       await updateGuard(finalData.id, finalData);
//       toast.success("Officer updated successfully!");
//       onBack();
//     } catch (error) {
//       console.error(error);
//       toast.error("Failed to update Officer");
//     }
//   };

//   return (
//     <form onSubmit={handle_gd_submit}>
//       <h2>Edit Officer</h2>

//       <FormInput label="Full Name" name="name" value={guardformData.name} onChange={handle_gd_change} isEdit />
//       <FormInput label="Email" name="email" value={guardformData.email} onChange={handle_gd_change} isEdit />
//       <FormInput label="Username" name="username" value={guardformData.username} onChange={handle_gd_change} isEdit />
//       <FormInput label="Password" name="password" type="text" value={guardformData.password} onChange={handle_gd_change} isEdit />

//       {role === "admin" && (
//         <>
//           <div className="form-group">
//             <label>Officer Rank</label>
//             <select
//               name="rank"
//               value={guardformData.rank || ""}
//               onChange={handle_gd_change}
//               style={selectStyle}
//               required
//             >
//               <option value="">Select Rank</option>
//               {ranks.map((rank) => (
//                 <option key={rank} value={rank}>{rank}</option>
//               ))}
//               <option value="Other">Other</option>
//             </select>
//           </div>

//           {showOtherRank && (
//             <FormInput
//               label="Enter Custom Rank"
//               name="otherRank"
//               value={otherRank}
//               onChange={(e) => setOtherRank(e.target.value)}
//               isEdit
//             />
//           )}
//         </>
//       )}

//       <FormInput label="Guard Experience" name="experience" type="text" value={guardformData.experience || ""} onChange={handle_gd_change} isEdit />
//       <FormInput label="Contact No" name="contactno" value={guardformData.contactno} onChange={handle_gd_change} isEdit />

//       <SubmitButton label="Update" />

//       <button type="button" onClick={onBack} className="btn btn-secondary w-100 mt-3">
//         Cancel
//       </button>
//     </form>
//   );
// }

// /* -------- PROPTYPES -------- */
// GuardEditForm.propTypes = {
//   guardData: PropTypes.object.isRequired,
//   onBack: PropTypes.func.isRequired,
// };

// const selectStyle = {
//   width: "100%",
//   padding: "8px",
//   marginTop: "5px",
//   borderRadius: "5px",
//   border: "1px solid #ccc",
// };
